<template>
  <div
    ref="scrollRef"
    role="none"
    class="bitts-virtual-scroll"
    :class="{
      'scrollable-down': useScrollFade && isScrollable && !arrivedState.bottom,
      'scrollable-up': useScrollFade && isScrollable && !arrivedState.top,
    }"
    :style="{ width: calculatedWidth, height: `${calculatedHeight}px` }"
  >
    <!--
      The wrappers are role="none" so a role="listbox" around the list still
      finds its options: Safari only looks through ignored or group elements,
      and inside a popover it kept these divs, so it exposed the listbox as a
      plain group and VoiceOver read no position or selection for its options.
      @see https://github.com/WebKit/WebKit/blob/main/Source/WebCore/accessibility/AXCoreObject.cpp listBoxInterpretation (2026-10-13)
    -->
    <div
      role="none"
      class="bitts-virtual-scroll__inner"
      :style="{ height: `${virtualizer.getTotalSize()}px` }"
    >
      <div
        v-for="item in virtualItems"
        :key="String(item.key)"
        :ref="measureItem"
        :data-index="item.index"
        role="none"
        class="bitts-virtual-scroll__item"
        :style="{
          ...(hasDynamicItemHeight ? {} : { height: `${item.size}px` }),
          transform: `translateY(${item.start}px)`,
        }"
      >
        <slot :item="options[item.index]!" :index="item.index" />
      </div>
    </div>
  </div>
</template>

<script setup lang="ts" generic="TData">
import { useVirtualizer } from '@tanstack/vue-virtual';
import { useScroll } from '@vueuse/core';
import { type ComponentPublicInstance, computed, ref } from 'vue';

export type SelectOption = {
  value: string | number;
  label: string;
};

type Props = {
  options: readonly TData[];
  /**
   * Maximum height of the scrollable container.
   * Container will shrink if content is smaller.
   */
  maxHeight?: number;
  /**
   * Height of each item in pixels.
   * @default 36
   */
  itemHeight?: number;
  /**
   * Number of items to render outside the visible area.
   * Higher values reduce flicker during fast scrolling.
   * @default 5
   */
  overscan?: number;
  /**
   * If true, calculates the width based on the longest option label.
   */
  useMaxWidth?: boolean;
  /**
   * Base padding (in px) to account for checkbox, icons, and container padding.
   * @default 80
   */
  bufferPx?: number;
  /**
   * Approximate width (in px) of a single character.
   * @default 8
   */
  charWidth?: number;
  /**
   * Minimum width constraint (in px).
   * @default 280
   */
  minWidth?: number;
  /**
   * Maximum width constraint (in px).
   * @default 600
   */
  maxWidth?: number;
  /**
   * Enable scroll fade masks at top/bottom edges.
   * @default false
   */
  useScrollFade?: boolean;
  /**
   * Stable key for each option, so a row keeps its state when the options
   * are filtered or scrolled. Rows are keyed by index when omitted.
   */
  getItemKey?: (item: TData, index: number) => string | number;
  /**
   * Measure each rendered row instead of fixing it at itemHeight, so rows
   * whose content wraps or grows with text size take the height they need.
   * itemHeight is then only the estimate for rows not yet rendered.
   * @default false
   */
  hasDynamicItemHeight?: boolean;
};

const props = withDefaults(defineProps<Props>(), {
  maxHeight: 320,
  itemHeight: 36,
  overscan: 5,
  useMaxWidth: false,
  bufferPx: 80,
  charWidth: 8,
  minWidth: 280,
  maxWidth: 600,
  useScrollFade: false,
  getItemKey: undefined,
  hasDynamicItemHeight: false,
});

defineSlots<{
  default(props: { item: TData; index: number }): unknown;
}>();

const scrollRef = ref<HTMLElement | null>(null);

const { arrivedState } = useScroll(scrollRef);
const isScrollable = computed(() => !arrivedState.top || !arrivedState.bottom);

const virtualizer = useVirtualizer(
  computed(() => {
    const { getItemKey } = props;
    return {
      count: props.options.length,
      getScrollElement: () => scrollRef.value,
      estimateSize: () => props.itemHeight,
      overscan: props.overscan,
      ...(getItemKey && {
        getItemKey: (index: number) =>
          getItemKey(props.options[index] as TData, index),
      }),
    };
  }),
);

const virtualItems = computed(() => virtualizer.value.getVirtualItems());

function measureItem(element: Element | ComponentPublicInstance | null) {
  if (props.hasDynamicItemHeight && element instanceof Element) {
    virtualizer.value.measureElement(element);
  }
}

const calculatedWidth = computed(() => {
  if (!props.useMaxWidth || !props.options.length) {
    return undefined;
  }

  const maxChars = props.options.reduce((max, item) => {
    const label = (item as { label?: string }).label;
    return label && label.length > max ? label.length : max;
  }, 0);

  const estimatedWidth = maxChars * props.charWidth + props.bufferPx;

  const finalWidth = Math.min(
    Math.max(estimatedWidth, props.minWidth),
    props.maxWidth,
  );

  return `${finalWidth}px`;
});

const calculatedHeight = computed(() => {
  const totalHeight = props.hasDynamicItemHeight
    ? virtualizer.value.getTotalSize()
    : props.options.length * props.itemHeight;
  return Math.min(totalHeight, props.maxHeight);
});

/**
 * Scroll to a specific index.
 * Useful for keyboard navigation.
 */
function scrollToIndex(
  index: number,
  options?: { align?: 'start' | 'center' | 'end' | 'auto' },
) {
  virtualizer.value.scrollToIndex(index, options);
}

defineExpose({
  scrollToIndex,
  virtualizer,
});
</script>

<style scoped lang="pcss">
@reference "../../../../apps/crossbeam-app/src/css/theme.css";
.bitts-virtual-scroll {
  @apply overflow-y-auto overflow-x-hidden relative;
}

.bitts-virtual-scroll.scrollable-up.scrollable-down {
  mask-image: linear-gradient(to bottom, transparent 0%, black 15%),
    linear-gradient(to top, transparent 0%, black 15%);
  mask-composite: intersect;
}

.bitts-virtual-scroll.scrollable-up:not(.scrollable-down) {
  mask-image: linear-gradient(to bottom, transparent 0%, black 15%);
}

.bitts-virtual-scroll.scrollable-down:not(.scrollable-up) {
  mask-image: linear-gradient(to top, transparent 0%, black 15%);
}

.bitts-virtual-scroll__inner {
  @apply w-full relative;
}

.bitts-virtual-scroll__item {
  @apply absolute top-0 left-0 w-full flex items-center;
}
</style>
